import {
  Box,
  Paper,
  Typography,
  InputAdornment,
  IconButton,
  TextField,
  useTheme,
  CircularProgress,
} from '@mui/material'
import { Button } from '@ds/components'
import { useState } from 'react'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import { type Control, type FieldErrors, Controller } from 'react-hook-form'
import { LOGIN_FORM_STRINGS } from './strings'

type TLoginFormData = {
  email: string
  password: string
}

type TLoginFormProps = {
  control: Control<TLoginFormData>
  errors: FieldErrors<TLoginFormData>
  isValid: boolean
  isLoading: boolean
  onForgotPasswordClick: () => void
  onSubmit: (e: React.FormEvent) => void
}

export const LoginForm = ({
  control,
  errors,
  isValid,
  isLoading,
  onForgotPasswordClick,
  onSubmit,
}: TLoginFormProps) => {
  const theme = useTheme()
  const [showPassword, setShowPassword] = useState(false)

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        pt: { xs: 12, sm: 8, md: 6 },
      }}
    >
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, sm: 4, md: 5 },
          minWidth: { xs: '90%', sm: 400, md: 480 },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          bgcolor: 'background.default',
        }}
      >
        <Typography variant="h4" sx={{ mb: 4, fontWeight: 500 }}>
          {LOGIN_FORM_STRINGS.title}
        </Typography>
        <Box component="form" sx={{ width: '100%' }} onSubmit={onSubmit}>
          <Controller
            name="email"
            control={control}
            rules={{
              required: LOGIN_FORM_STRINGS.emailRequired,
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: LOGIN_FORM_STRINGS.emailInvalid,
              },
            }}
            render={({ field }) => (
              <TextField
                {...field}
                label={LOGIN_FORM_STRINGS.emailLabel}
                variant="outlined"
                fullWidth
                sx={{
                  mb: 2,
                  '& .MuiFormHelperText-root': {
                    minHeight: '20px',
                    margin: '4px 0 0 0',
                  },
                }}
                autoComplete="email"
                error={!!errors.email}
                helperText={errors.email?.message || ' '}
              />
            )}
          />
          <Controller
            name="password"
            control={control}
            rules={{
              required: LOGIN_FORM_STRINGS.passwordRequired,
            }}
            render={({ field }) => (
              <TextField
                {...field}
                label={LOGIN_FORM_STRINGS.passwordLabel}
                variant="outlined"
                type={showPassword ? 'text' : 'password'}
                fullWidth
                sx={{
                  mb: 3,
                  '& .MuiFormHelperText-root': {
                    minHeight: '20px',
                    margin: '4px 0 0 0',
                  },
                }}
                autoComplete="current-password"
                error={!!errors.password}
                helperText={errors.password?.message || ' '}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(show => !show)}
                          edge="end"
                          size="small"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
              />
            )}
          />
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Button
              variant="text"
              sx={{ color: theme.palette.text.primary }}
              onClick={onForgotPasswordClick}
            >
              {LOGIN_FORM_STRINGS.forgotPassword}
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              sx={{ px: 3 }}
              disabled={isLoading || !isValid}
              startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {isLoading ? LOGIN_FORM_STRINGS.loggingInButton : LOGIN_FORM_STRINGS.loginButton}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  )
}
